import React, { useContext, useState, useRef, useEffect } from 'react';
import { Table, Dropdown, Modal, Button } from 'react-bootstrap';
import ReportsService from '../../../../../../../../services/reports-service';
import './corporate-actions-reports.scss';
import { IdentityContext } from '../../../../../../../../context/identity-context';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import { ExcelPreviewModal } from '../adhoc-reports/components/modals/excel-preview-modal'; 

const CorporateActionsReports = ({ reports, itemIsin }) => {
  const [expandedReport, setExpandedReport] = useState(null);
  const dropdownRefs = useRef([]);
  const reportService = new ReportsService();
  
  const { user } = useContext(IdentityContext);

  // State for modal and loading
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportToDownload, setReportToDownload] = useState(null);

  // States for Excel Preview Modal
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false);

  // Helper function to return an embed URL for XLSX files
  const getEmbedUrl = (url) => {
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname.toLowerCase();
      if (pathname.endsWith('.xlsx')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    } catch (e) {
      const strippedUrl = url.split('?')[0].toLowerCase();
      if (strippedUrl.endsWith('.xlsx')) {
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      }
    }
    return url;
  };

  const handleDropdownToggle = (id) => {
    setExpandedReport(prev => (prev === id ? null : id));
  };

  // Modified preview function now works like in InterestBasedReportContainer
  const handlePreview = async (report) => {
    try {
      const response = await reportService.retrieveReport(report.type, itemIsin, report.name, report.paymentDate);
      const fileURL = response.fileURL;

      // Check if it's an Excel file and show the preview inside the modal
      const urlObj = new URL(fileURL);
      if (urlObj.pathname.toLowerCase().endsWith('.xlsx')) {
        const fileResponse = await fetch(fileURL);
        const blob = await fileResponse.blob();
        const arrayBuffer = await blob.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const spreadsheetData = jsonData.map(row => row.map(cell => ({ value: cell, readOnly: true })));
        
        setExcelPreviewData(spreadsheetData);
        setShowExcelPreviewModal(true);
      } else {
        // Handle non-Excel files by opening them in a new tab
        const embedUrl = getEmbedUrl(fileURL);
        window.open(embedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error fetching report preview:', error);
    }
  };

  const handleDownloadConfirmation = (report) => {
    setReportToDownload(report);
    setShowModal(true);
  };

  const handleDownload = async () => {
    setLoading(true);
    
    try {
      const issuerName = user.attributes['custom:display_name'];
      const report = reportToDownload;
      const response = await reportService.retrieveReportIssuer(report.type, itemIsin, report.name, report.paymentDate, issuerName);
      
      const fileResponse = await fetch(response.fileURL);
      if (!fileResponse.ok) throw new Error('Network response was not ok');
      
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = response.fileName || 'download.pdf';
      link.href = objectUrl;
      link.download = fileName; 
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error fetching report download:', error);
    } finally {
      setShowModal(false);
      setLoading(false);
      window.location.reload(); // Reload the page after download
    }
  };

  // Helper function to render the status cell with dropdown functionalities
  const renderStatusCell = (report, index) => {
    return report.status === 'No Reports to Display' ? (
      report.status
    ) : (
      <div className="status-container" ref={el => (dropdownRefs.current[index] = el)}>
        <span className="status-text">{report.status}</span>
        {report.status === 'Reports Available' && (
          <Dropdown show={expandedReport === report.id}>
            <Dropdown.Toggle
              variant="link"
              className="dropdown-arrow-button"
              onClick={() => handleDropdownToggle(report.id)}
            >
              ▼
            </Dropdown.Toggle>
            {expandedReport === report.id &&
              ReactDOM.createPortal(
                <div className="dropdown-overlay">
                  <Dropdown.Menu className="dropdown-menu" id="dropdown-menu">
                    <Dropdown.Item onClick={() => {
                      handlePreview(report);
                      setExpandedReport(null);
                    }}>
                      Preview
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => {
                      handleDownloadConfirmation(report);
                      setExpandedReport(null);
                    }}>
                      Download
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </div>,
                document.body
              )}
          </Dropdown>
        )}
      </div>
    );
  };

  // Filter reports with a payment date only
  const reportsWithPaymentDate = reports.filter(report => report.paymentDate);

  // Close the dropdown if clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRefs.current.some(ref => ref && ref.contains(event.target))) {
        return;
      }
      if (document.getElementById('dropdown-menu') && document.getElementById('dropdown-menu').contains(event.target)) {
        return;
      }
      setExpandedReport(null);
    };

    if (expandedReport !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expandedReport]);

  return (
    <div className='reportType-table'>
      {/* Table for reports with a payment date */}
      <Table striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: '50%' }}>Report Name</th>
            <th style={{ width: '20%' }}>Payment Date</th>
            <th style={{ width: '30%' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {reportsWithPaymentDate.length > 0 ? (
            reportsWithPaymentDate.map((report, index) => (
              <tr key={report.id}>
                <td>{report.name}</td>
                <td>{report.paymentDate}</td>
                <td className="status-cell">
                  {renderStatusCell(report, index)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" className="text-center">No Reports Available</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Modal for download confirmation */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Warning: Authorized Access Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            By proceeding to download this report, you confirm that you are an authorized individual with permission to access this sensitive information.
          </p>
          <p>
            Please note that once the report is downloaded, it will no longer be available for viewing through this platform. Ensure that you have the necessary permissions and are ready to download before continuing.
          </p>
          <p>Do you wish to proceed?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleDownload} disabled={loading}>
            {loading ? 'Downloading..' : 'Continue'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => setShowExcelPreviewModal(false)}
        />
      )}
    </div>
  );
};

export default CorporateActionsReports;
